import { Grid, List, Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  GynecologicalPhysicalExamAnswerType,
  GynecologicalPhysicalExamDiffResult,
  GynecologicalPhysicalExamNotesAnswer,
  GynecologicalPhysicalExamSectionAnswer,
  GynecologicalPhysicalExamSingleChoiceAnswer,
} from "@/physical-exams/services/gynecological-physical-exam-diff";
import { booleanToYesOrNo } from "@/utils/medplum";

interface GynecologicalPhysicalExamsDiffGridProps {
  title: string;
  histories: GynecologicalPhysicalExamDiffResult;
}

const GynecologicalPhysicalExamsDiffGrid = ({
  title,
  histories,
}: GynecologicalPhysicalExamsDiffGridProps) => {
  const historyData = histories[title];
  if (!historyData) return null;

  /* eslint-disable quilted/require-responsive-grid -- the previous and amended values sit side by side at every width, since comparing them is the point, and each half stays readable at 375px */
  return historyData.map(([currentAnswers, previousAnswers], index) => {
    if (currentAnswers?.type === GynecologicalPhysicalExamAnswerType.SECTION) {
      const previous = previousAnswers as GynecologicalPhysicalExamSectionAnswer;
      const current = currentAnswers as GynecologicalPhysicalExamSectionAnswer;

      return (
        <Grid p={24} key={`${title}-${index}-grid`}>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Stack>
              <TextWrapper variant="bold">
                {current?.sectionName || previous?.sectionName || `Section ${index + 1}`}
              </TextWrapper>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            {current?.declined ? (
              <TextWrapper pb={8} c="dimmed">
                Declined
              </TextWrapper>
            ) : (
              <Grid>
                <Grid.Col span={6}>
                  <TextWrapper pb={8} c="dimmed">
                    Previous Normal
                  </TextWrapper>
                  <List my={4} mx={16}>
                    {previous?.normalDetails?.map((detail: string, i: number) => (
                      <List.Item key={`previous-normal-${i}`}>
                        <WordsDiff
                          showRemoved
                          previous={detail}
                          current={current?.normalDetails?.[i]}
                        />
                      </List.Item>
                    )) || []}
                  </List>
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextWrapper pb={8} c="dimmed">
                    Amended Normal
                  </TextWrapper>
                  <List my={4} mx={16}>
                    {current?.normalDetails?.map((detail: string, i: number) => (
                      <List.Item key={`current-normal-${i}`}>
                        <WordsDiff current={detail} previous={previous?.normalDetails?.[i]} />
                      </List.Item>
                    )) || []}
                  </List>
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextWrapper pb={8} c="dimmed">
                    Previous Not Normal
                  </TextWrapper>
                  <List my={4} mx={16}>
                    {previous?.notNormalDetails?.map((detail: string, i: number) => (
                      <List.Item key={`previous-not-normal-${i}`}>
                        <WordsDiff
                          showRemoved
                          previous={detail}
                          current={current?.notNormalDetails?.[i]}
                        />
                      </List.Item>
                    )) || []}
                  </List>
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextWrapper pb={8} c="dimmed">
                    Amended Not Normal
                  </TextWrapper>
                  <List my={4} mx={16}>
                    {current?.notNormalDetails?.map((detail: string, i: number) => (
                      <List.Item key={`current-not-normal-${i}`}>
                        <WordsDiff current={detail} previous={previous?.notNormalDetails?.[i]} />
                      </List.Item>
                    )) || []}
                  </List>
                </Grid.Col>
              </Grid>
            )}
          </Grid.Col>
        </Grid>
      );
    }

    if (currentAnswers?.type === GynecologicalPhysicalExamAnswerType.SINGLE_CHOICE) {
      const previous = previousAnswers as GynecologicalPhysicalExamSingleChoiceAnswer;
      const current = currentAnswers as GynecologicalPhysicalExamSingleChoiceAnswer;

      return (
        <Grid p={24} key={`${title}-${index}-single-choice-grid`}>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Stack>
              <TextWrapper variant="bold">
                {current?.sectionName || previous?.sectionName || `Section ${index + 1}`}
              </TextWrapper>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            {current?.declined ? (
              <TextWrapper pb={8} c="dimmed">
                Declined
              </TextWrapper>
            ) : (
              <Grid>
                <Grid.Col span={6}>
                  <TextWrapper pb={8} c="dimmed">
                    Previous Single Choice
                  </TextWrapper>
                  <TextWrapper mb={8} size="sm" c="dimmed">
                    <WordsDiff
                      showRemoved
                      current={booleanToYesOrNo(current?.singleChoice)}
                      previous={booleanToYesOrNo(previous?.singleChoice)}
                    />
                  </TextWrapper>
                  <List my={4} mx={16}>
                    {previous?.singleChoiceDetails?.map((detail: string, i: number) => (
                      <List.Item key={`previous-single-choice-${i}`}>
                        <WordsDiff
                          showRemoved
                          previous={detail}
                          current={current?.singleChoiceDetails?.[i]}
                        />
                      </List.Item>
                    )) || []}
                  </List>
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextWrapper pb={8} c="dimmed">
                    Amended Single Choice
                  </TextWrapper>
                  <TextWrapper mb={8} size="sm" c="dimmed">
                    <WordsDiff
                      current={booleanToYesOrNo(current?.singleChoice)}
                      previous={booleanToYesOrNo(previous?.singleChoice)}
                    />
                  </TextWrapper>
                  <List my={4} mx={16}>
                    {current?.singleChoiceDetails?.map((detail: string, i: number) => (
                      <List.Item key={`current-single-choice-${i}`}>
                        <WordsDiff current={detail} previous={previous?.singleChoiceDetails?.[i]} />
                      </List.Item>
                    )) || []}
                  </List>
                </Grid.Col>
              </Grid>
            )}
          </Grid.Col>
        </Grid>
      );
    }

    // Handle notes
    const previous = previousAnswers as GynecologicalPhysicalExamNotesAnswer;
    const current = currentAnswers as GynecologicalPhysicalExamNotesAnswer;

    return (
      (previous?.notes || current?.notes) && (
        <Grid p={24} key={`${title}-notes`}>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <TextWrapper variant="bold">Notes</TextWrapper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <Grid>
              <Grid.Col span={6}>
                <TextWrapper pb={8} c="dimmed">
                  Previous Notes
                </TextWrapper>
                <WordsDiff showRemoved current={current?.notes} previous={previous?.notes} />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextWrapper pb={8} c="dimmed">
                  Amended Notes
                </TextWrapper>
                <WordsDiff current={current?.notes} previous={previous?.notes} />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      )
    );
  });
};

/* eslint-enable quilted/require-responsive-grid */

export default GynecologicalPhysicalExamsDiffGrid;
